'use client';

import { useMemo, useState } from 'react';

import RichText from '@/components/ui/RichText/RichText';

import BlogCardItem from '../BlogCardItem/BlogCardItem';
import type { BlogCard } from '../../data/types';

type BlogsSectionProps = {
  blogs: BlogCard[];
  title?: string;
  description?: string;
  pageSize?: number;
  wrapperClassName?: string;
};

export default function BlogsSection({
  blogs,
  title,
  description,
  pageSize = 2,
  wrapperClassName = '',
}: BlogsSectionProps) {
  const [currentPage, setCurrentPage] = useState(1);

  const totalPages = Math.max(1, Math.ceil(blogs.length / pageSize));

  const visibleBlogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;

    return blogs.slice(start, start + pageSize);
  }, [blogs, currentPage, pageSize]);

  if (!blogs.length) {
    return null;
  }

  return (
    <section className={`${wrapperClassName} py-4 md:py-5`}>
      {(title || description) && (
        <div className="mb-8">
          {title && (
            <RichText
              styles={{
                h2: 'text-3xl md:text-4xl font-bold text-royal-875',
              }}
            >
              {`## ${title}`}
            </RichText>
          )}

          {description && (
            <RichText
              className="mt-3"
              styles={{
                p: 'text-default-body',
              }}
            >
              {description}
            </RichText>
          )}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {visibleBlogs.map((blog) => (
          <BlogCardItem key={blog.slug} blog={blog} />
        ))}
      </div>

      {totalPages > 1 && (
        <div className="mt-10 flex items-center justify-center gap-2">
          <button
            type="button"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage((page) => page - 1)}
            className="h-10 w-10 rounded-full border border-gray-300 font-semibold text-gray-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            ←
          </button>

          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              type="button"
              onClick={() => setCurrentPage(page)}
              className={`h-10 w-10 rounded-full font-semibold transition ${
                page === currentPage
                  ? 'bg-royal-500 border-royal-500 border text-white'
                  : 'border border-gray-300 text-gray-500 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600'
              }`}
            >
              {page}
            </button>
          ))}

          <button
            type="button"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage((page) => page + 1)}
            className="h-10 w-10 rounded-full border border-gray-300 font-semibold text-gray-500 transition hover:border-blue-200 hover:bg-blue-50 hover:text-blue-600 disabled:cursor-not-allowed disabled:opacity-40"
          >
            →
          </button>
        </div>
      )}
    </section>
  );
}
